// ИКЛ.1 этап 3: панель кошелька USDT.
//
// Используется внутри модалки «БАЛАНС USDT» на главной.
// Показывает баланс + переключатель Пополнить / Вывести / История.

import { useEffect, useState } from 'react';
import {
  getWalletInfo, listWithdrawals, requestWithdraw, listTransactions, buildDepositTx,
  type WalletInfoResponse, type WithdrawalItem, type TransactionDto
} from '../api/client';
import { useToast } from './Toast';
import { useTelegram } from '../telegram/useTelegram';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';

type Tab = 'deposit' | 'withdraw' | 'history';

const STATUS_LABEL: Record<string, string> = {
  pending: '⏳ В очереди',
  processing: '⚙ Обрабатывается',
  sent: '✓ Отправлено',
  failed: '✖ Ошибка',
  cancelled: '⛔ Отменено'
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'var(--t-mid)',
  processing: 'var(--neon-cy)',
  sent: 'var(--neon-li)',
  failed: '#ff7a7a',
  cancelled: 'var(--t-lo)'
};

function shortAddr(a: string): string {
  return a.length > 12 ? a.slice(0, 6) + '…' + a.slice(-6) : a;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function WalletPanel({ tonAddress }: { tonAddress?: string | null }) {
  const [tab, setTab] = useState<Tab>('deposit');
  const [info, setInfo] = useState<WalletInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();

  const refresh = async () => {
    try {
      const r = await getWalletInfo();
      setInfo(r);
    } catch { /* offline */ }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  if (loading || !info) {
    return <div style={{ color: 'var(--t-lo)', textAlign: 'center', padding: 16 }}>Загрузка кошелька…</div>;
  }

  const availableUsdt = (info.balance.available_cents / 100).toFixed(2);
  const lockedUsdt = (info.balance.locked_cents / 100).toFixed(2);
  const totalUsdt = (info.balance.total_cents / 100).toFixed(2);

  return (
    <>
      {/* Баланс */}
      <div style={{ textAlign: 'center', padding: '12px 0 16px' }}>
        <div style={{ color: '#26A17B', fontSize: 32, fontWeight: 700 }}>
          ${availableUsdt}
        </div>
        <div style={{ fontSize: 11, color: 'var(--t-lo)', letterSpacing: 1 }}>
          USDT доступно
        </div>
        {info.balance.locked_cents > 0 && (
          <div style={{ fontSize: 10, color: 'var(--t-mute, #888)', marginTop: 4 }}>
            из ${totalUsdt} (${lockedUsdt} заморожено)
          </div>
        )}
      </div>

      {/* Табы */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {([
          { k: 'deposit',  label: '↓ Пополнить' },
          { k: 'withdraw', label: '↑ Вывести' },
          { k: 'history',  label: 'История' }
        ] as const).map(t => (
          <button
            key={t.k}
            onClick={() => { hapticTap(); setTab(t.k); }}
            className="cd"
            style={{
              flex: 1, padding: '8px 6px',
              background: tab === t.k ? 'var(--neon-cy)' : 'transparent',
              color: tab === t.k ? '#06131A' : 'var(--t-mid)',
              border: `0.5px solid ${tab === t.k ? 'var(--neon-cy)' : 'var(--line, #555)'}`,
              fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
              cursor: 'pointer'
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'deposit' && <DepositBlock info={info} onCopy={(label) => toast.show(label, 'green')} />}
      {tab === 'withdraw' && <WithdrawBlock info={info} tonAddress={tonAddress} onSubmitted={() => {
        hapticSuccess(); toast.show('Заявка создана', 'green'); refresh();
      }} onError={(msg) => { hapticError(); toast.show(msg, 'red'); }} />}
      {tab === 'history' && <HistoryBlock />}
    </>
  );
}

// === Блок Пополнить ===
function DepositBlock({ info, onCopy }: { info: WalletInfoResponse; onCopy: (label: string) => void }) {
  const [tonConnectUI] = useTonConnectUI();
  const tonAddress = useTonAddress();
  const [amountStr, setAmountStr] = useState('1.00');
  const [busy, setBusy] = useState(false);
  const copy = (text: string, label: string) => {
    navigator.clipboard?.writeText(text);
    onCopy(label + ' скопирован');
  };

  // ИКЛ.1: пополнение в один тап через TonConnect.
  // Сервер строит готовый BOC (Jetton transfer на наш wallet с memo
  // игрока). TonConnect открывает Tonkeeper с заполненной транзакцией —
  // юзер только подтверждает.
  const sendViaTonConnect = async () => {
    const amount = parseFloat(amountStr.replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0.10) {
      onCopy('Минимум $0.10');
      return;
    }
    setBusy(true);
    try {
      const tx = await buildDepositTx(tonAddress, Math.round(amount * 100));
      await tonConnectUI.sendTransaction({
        validUntil: tx.validUntil,
        messages: tx.messages
      });
      onCopy('Транзакция отправлена. Баланс обновится через 30-60 сек');
    } catch (e: any) {
      const msg = String(e?.message || e);
      if (msg.includes('UserRejected') || msg.includes('rejected')) {
        // Юзер сам отменил — не показываем как ошибку.
      } else {
        onCopy('Не получилось: ' + msg.slice(0, 80));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* TonConnect-путь (показывается только если кошелёк подключён) */}
      {tonAddress ? (
        <div style={{
          padding: 12, background: 'rgba(61,240,255,0.06)',
          border: '0.5px solid var(--neon-cy, #3DF0FF)',
          display: 'flex', flexDirection: 'column', gap: 8
        }}>
          <div style={{ fontSize: 11, color: 'var(--neon-cy, #3DF0FF)', letterSpacing: 0.4, fontWeight: 700, textTransform: 'uppercase' }}>
            ⚡ Быстрое пополнение через Tonkeeper
          </div>
          <div style={{ fontSize: 11, color: 'var(--t-mid)', lineHeight: 1.5 }}>
            Подключённый TON-кошелёк: <code style={{ color: 'var(--t-hi)' }}>{tonAddress.slice(0,4)}…{tonAddress.slice(-4)}</code>.
            Тапни — Tonkeeper откроется с готовой транзакцией.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--t-mid)' }}>$</span>
            <input
              type="number" inputMode="decimal" step="0.10" min="0.10"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              disabled={busy}
              style={{
                flex: 1,
                background: 'var(--bg-3, #0a0a14)',
                color: 'var(--t-hi)',
                border: '0.5px solid var(--line, #444)',
                padding: '8px 10px', fontFamily: 'inherit', fontSize: 14, outline: 'none'
              }}
            />
            <button
              onClick={sendViaTonConnect}
              disabled={busy}
              className="cd"
              style={{
                background: 'var(--neon-cy, #3DF0FF)', color: '#06131A', border: 'none',
                padding: '10px 14px', fontSize: 11, fontWeight: 700,
                letterSpacing: 0.4, textTransform: 'uppercase',
                cursor: busy ? 'default' : 'pointer',
                opacity: busy ? 0.5 : 1
              }}
            >{busy ? '…' : '↗ Отправить'}</button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--t-lo)' }}>
            Газ ~0.05 TON платит твой кошелёк. Memo подставится автоматически.
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--t-lo)', fontStyle: 'italic', padding: '8px 0' }}>
          💡 Подключи TON-кошелёк (кнопка TON на главной) — появится быстрое
          пополнение в один тап.
        </div>
      )}

      <div style={{ fontSize: 10, color: 'var(--t-mute, #888)', letterSpacing: 0.4, textAlign: 'center', margin: '4px 0' }}>
        ── ИЛИ ВРУЧНУЮ ──
      </div>

      <div style={{ fontSize: 12, color: 'var(--t-mid)', lineHeight: 1.5 }}>
        Открой Tonkeeper / Wallet, выбери <strong style={{ color: 'var(--t-hi)' }}>USDT</strong> в сети TON,
        отправь любую сумму на адрес ниже. <strong style={{ color: '#ffd166' }}>Обязательно</strong> вставь
        memo в комментарий — без него зачисление не сработает.
      </div>

      <div>
        <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 0.5, marginBottom: 4 }}>
          АДРЕС ПОЛУЧАТЕЛЯ
        </div>
        <div style={{
          background: 'var(--bg-3, #0a0a14)', padding: '10px 12px',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
          color: 'var(--t-hi)', wordBreak: 'break-all',
          border: '0.5px solid var(--line, #444)'
        }}>{info.deposit.to_address}</div>
        <button
          onClick={() => copy(info.deposit.to_address, 'Адрес')}
          className="cd"
          style={{
            marginTop: 6, width: '100%', padding: '8px',
            background: 'transparent', color: 'var(--neon-cy)',
            border: '0.5px solid var(--neon-cy)',
            fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
            cursor: 'pointer'
          }}
        >📋 Копировать адрес</button>
      </div>

      <div>
        <div style={{ fontSize: 10, color: '#ffd166', letterSpacing: 0.5, marginBottom: 4 }}>
          MEMO / КОММЕНТАРИЙ ⚠ обязательно
        </div>
        <div style={{
          background: 'var(--bg-3, #0a0a14)', padding: '10px 12px',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 700,
          color: '#ffd166', textAlign: 'center',
          border: '0.5px solid #ffd166'
        }}>{info.deposit.memo}</div>
        <button
          onClick={() => copy(info.deposit.memo, 'Memo')}
          className="cd"
          style={{
            marginTop: 6, width: '100%', padding: '8px',
            background: '#ffd166', color: '#06131A', border: 'none',
            fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
            cursor: 'pointer'
          }}
        >📋 Копировать memo</button>
      </div>

      <div style={{ fontSize: 10, color: 'var(--t-lo)', lineHeight: 1.6, marginTop: 4 }}>
        {info.deposit.notes.map((n, i) => <div key={i}>· {n}</div>)}
      </div>
    </div>
  );
}

// === Блок Вывести ===
function WithdrawBlock({ info, tonAddress, onSubmitted, onError }: {
  info: WalletInfoResponse;
  tonAddress?: string | null;
  onSubmitted: () => void;
  onError: (msg: string) => void;
}) {
  const [address, setAddress] = useState<string>(tonAddress || '');
  const [amountStr, setAmountStr] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<WithdrawalItem[]>([]);

  const refreshList = async () => {
    try { const r = await listWithdrawals(); setItems(r.items); } catch {}
  };
  useEffect(() => { refreshList(); }, []);

  const minUsdt = (info.withdrawal.min_cents / 100).toFixed(2);
  const feeUsdt = (info.withdrawal.fee_cents / 100).toFixed(2);
  const availableUsdt = (info.balance.available_cents / 100).toFixed(2);
  const remainingDailyUsdt = (info.withdrawal.available_today_cents / 100).toFixed(2);

  const onSubmit = async () => {
    const amount = parseFloat(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) return onError('Укажи сумму');
    const amountCents = Math.round(amount * 100);
    if (!address.match(/^[UE]Q[A-Za-z0-9_-]{46}$/)) return onError('Некорректный TON-адрес');
    setBusy(true);
    try {
      await requestWithdraw(address, amountCents);
      setAmountStr('');
      await refreshList();
      onSubmitted();
    } catch (e: any) {
      onError(e?.payload?.message || 'Не удалось создать заявку');
    } finally {
      setBusy(false);
    }
  };

  if (info.withdrawal.frozen) {
    return (
      <div style={{
        padding: 16, background: 'rgba(255,68,68,0.08)',
        border: '0.5px solid #ff7a7a', color: '#ff7a7a',
        fontSize: 12, textAlign: 'center', lineHeight: 1.5
      }}>
        ⛔ Вывод временно заморожен.<br />
        {info.withdrawal.frozen_reason && <span style={{ color: 'var(--t-mid)' }}>{info.withdrawal.frozen_reason}</span>}
        <br /><span style={{ color: 'var(--t-lo)', fontSize: 10 }}>Обратись в саппорт.</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--t-mid)', lineHeight: 1.5 }}>
        Минимум: <strong style={{ color: 'var(--t-hi)' }}>{minUsdt} USDT</strong>.
        Комиссия: <strong style={{ color: 'var(--t-hi)' }}>{feeUsdt} USDT</strong> (фиксированная).
        Списывается с баланса вместе с суммой.
      </div>

      <div>
        <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 0.5, marginBottom: 4 }}>
          АДРЕС TON-КОШЕЛЬКА (КУДА)
        </div>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value.trim())}
          placeholder="UQ... или EQ..."
          style={{
            width: '100%',
            background: 'var(--bg-3, #0a0a14)',
            color: 'var(--t-hi)',
            border: '0.5px solid var(--line, #444)',
            padding: '10px 12px', fontFamily: 'JetBrains Mono, monospace',
            fontSize: 12, letterSpacing: 0.2, outline: 'none'
          }}
        />
        {tonAddress && address !== tonAddress && (
          <button
            onClick={() => setAddress(tonAddress)}
            style={{
              marginTop: 4, background: 'transparent', color: 'var(--neon-cy)',
              border: 'none', fontSize: 10, padding: 0, cursor: 'pointer', letterSpacing: 0.3
            }}
          >↳ подставить подключённый ({shortAddr(tonAddress)})</button>
        )}
      </div>

      <div>
        <div style={{
          fontSize: 10, color: 'var(--t-lo)', letterSpacing: 0.5, marginBottom: 4,
          display: 'flex', justifyContent: 'space-between'
        }}>
          <span>СУММА USDT</span>
          <span>доступно: {availableUsdt} · сегодня лимит: {remainingDailyUsdt}</span>
        </div>
        <input
          type="number" inputMode="decimal" step="0.01" min={minUsdt}
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          placeholder={`не меньше ${minUsdt}`}
          style={{
            width: '100%',
            background: 'var(--bg-3, #0a0a14)',
            color: 'var(--t-hi)',
            border: '0.5px solid var(--line, #444)',
            padding: '10px 12px', fontFamily: 'inherit', fontSize: 14, outline: 'none'
          }}
        />
      </div>

      <button
        onClick={onSubmit}
        disabled={busy || !address || !amountStr}
        className="cd"
        style={{
          padding: '12px',
          background: 'var(--neon-li, #b4ff00)', color: '#06131A', border: 'none',
          fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
          cursor: (busy || !address || !amountStr) ? 'default' : 'pointer',
          opacity: (busy || !address || !amountStr) ? 0.5 : 1
        }}
      >{busy ? 'Создаём…' : 'Создать заявку на вывод'}</button>

      <div style={{ fontSize: 10, color: 'var(--t-lo)', lineHeight: 1.5 }}>
        После создания заявки баланс списывается сразу. Вывод обрабатывается
        в течение часа. Если транзакция не пройдёт — баланс вернётся.
      </div>

      {/* Список заявок */}
      {items.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: 'var(--t-md)', letterSpacing: 0.5, margin: '14px 0 6px' }}>
            МОИ ЗАЯВКИ
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.slice(0, 10).map(w => (
              <div key={w.id} style={{
                padding: '6px 10px', fontSize: 11,
                background: 'rgba(255,255,255,0.02)',
                borderLeft: `2px solid ${STATUS_COLOR[w.status] || 'var(--t-lo)'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: STATUS_COLOR[w.status] }}>{STATUS_LABEL[w.status] || w.status}</span>
                  <span style={{ color: 'var(--t-mid)' }}>${(w.amount_cents / 100).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--t-lo)', marginTop: 2 }}>
                  <span>→ {shortAddr(w.to_address)}</span>
                  <span>{fmtDate(w.createdAt)}</span>
                </div>
                {w.failure_reason && (
                  <div style={{ fontSize: 10, color: '#ff7a7a', marginTop: 2 }}>{w.failure_reason}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// === Блок История ===
function HistoryBlock() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<TransactionDto[]>([]);

  useEffect(() => {
    (async () => {
      try { const r = await listTransactions('usdt', 50); setItems(r); }
      catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ color: 'var(--t-lo)', textAlign: 'center', padding: 16 }}>Загрузка…</div>;
  if (items.length === 0) {
    return <div style={{ color: 'var(--t-lo)', textAlign: 'center', padding: 16, fontStyle: 'italic', fontSize: 12 }}>Операций пока нет</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map(t => {
        const positive = t.delta > 0;
        const sign = positive ? '+' : '';
        const reasonLabel = t.reason === 'deposit_ton' ? 'Пополнение TON'
          : t.reason === 'withdraw_request' ? 'Заявка на вывод'
          : t.reason === 'admin_grant' ? 'Подарок от админа'
          : t.reason.startsWith('shop_') ? 'Покупка в магазине'
          : t.reason;
        return (
          <div key={t.id} style={{
            padding: '6px 10px', fontSize: 11,
            display: 'flex', justifyContent: 'space-between',
            background: 'rgba(255,255,255,0.02)',
            borderLeft: `2px solid ${positive ? 'var(--neon-li)' : '#ff7a7a'}`
          }}>
            <div>
              <div style={{ color: 'var(--t-mid)' }}>{reasonLabel}</div>
              <div style={{ fontSize: 10, color: 'var(--t-lo)' }}>{fmtDate(t.createdAt)}</div>
            </div>
            <div style={{ color: positive ? 'var(--neon-li)' : '#ff7a7a', fontWeight: 600 }}>
              {sign}${(t.delta / 100).toFixed(2)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
